import { h, clear } from './lib/dom.js';
import { getState } from './store.js';
import { go, goReceive, goSend, openStatusPanel, openProfileMenu, retryWorkerConnection, toggleSidebar, goLock } from './actions.js';
import { applySwUpdate } from './sw-update.js';
import { iconMarkup } from './icons/index.js';
import { renderLanding } from './screens/landing.js';
import { renderHome } from './screens/home.js';
import { renderReceive } from './screens/receive.js';
import { renderSend } from './screens/send.js';
import { renderTransfer } from './screens/transfer.js';
import { renderActivity } from './screens/activity.js';
import { renderSettings } from './screens/settings.js';
import { renderRecipients } from './screens/recipients.js';
import { renderOpenAccountModal } from './modals/openAccount.js';
import { renderAddRecipientModal } from './modals/addRecipient.js';
import { renderImportKeyModal } from './modals/importKey.js';
import { renderConfirmRevealModal } from './modals/confirmReveal.js';
import { renderVerifyBackupModal } from './modals/verifyBackup.js';
import { renderStatusPanel } from './modals/statusPanel.js';
import { renderProfileMenu } from './modals/profileMenu.js';
import { renderLanguagePicker } from './modals/languagePicker.js';
import { renderLicenseModal } from './modals/license.js';
import { renderMainnetConfirmModal } from './modals/mainnetConfirm.js';
import { renderMigrateTreasuryModal } from './modals/migrateTreasury.js';
import { updateToast } from './toast.js';
import { t, isRTL } from './i18n/index.js';

const SCREENS = {
  home: renderHome,
  receive: renderReceive,
  move: renderSend,
  transfer: renderTransfer,
  txns: renderActivity,
  settings: renderSettings,
  recipients: renderRecipients
};

// Label resolved inside renderTabbar (below), not here — this array is
// module-level (evaluated once at import), so a t() call baked in here
// would freeze at whatever language was active on first import and never
// update on a language switch.
const TABS = [
  { screen: 'home', icon: 'home', labelKey: 'tabs.home' },
  { screen: 'receive', icon: 'receive', labelKey: 'tabs.receive' },
  { screen: 'move', icon: 'send', labelKey: 'tabs.send' },
  { screen: 'txns', icon: 'activity', labelKey: 'tabs.activity' },
  { screen: 'settings', icon: 'settings', labelKey: 'tabs.settings' }
];

function renderHeader(state) {
  const online = state.online;
  const statusIcon = online ? 'shieldCog' : 'shieldX';
  const statusColor = online ? '#1a8a4c' : '#c0392b';
  const activeProfile = state.profiles.find((p) => p.id === state.activeProfileId);
  const profileIcon = (activeProfile && activeProfile.avatar) || 'circleUserRound';
  return h('div', { class: 'header-bar' }, [
    h('button', { class: 'header-brand', 'aria-label': t('header.lockWallet'), onclick: goLock }, [
      h('img', { src: '/icons/icon.svg', alt: '' }),
      h('span', {}, 'Tynti'),
      // The one cross-screen signal Settings' Testnet toggle drives directly
      // — a real network-switching confirmation flow on Send/Transfer's
      // review screens (per CLAUDE.md: "mainnet actions need a visually
      // distinct, explicit confirmation step") is a larger, separate change
      // to those screens, not something this Settings-only pass reaches.
      // feature-mainnet.md: production defaults to mainnet, so mainnet is
      // now the common case — badging it on every load added little.
      // Testnet is the state that's actually easy to misjudge post-flip (a
      // "successful" send there moves nothing real but looks identical to
      // one that does), so the badge now marks *that* state instead.
      state.testnetEnabled ? h('span', { class: 'testnet-badge' }, t('header.testnetBadge')) : null
    ]),
    h('div', { class: 'header-icons' }, [
      h('button', {
        class: 'status-icon-btn',
        'aria-label': online ? t('header.statusOnline') : t('header.statusOffline'),
        onclick: openStatusPanel
      }, h('span', { html: iconMarkup(statusIcon, { size: 22, color: statusColor }) })),
      h('button', {
        class: 'status-icon-btn',
        'aria-label': t('header.switchProfile'),
        onclick: openProfileMenu
      }, h('span', { html: iconMarkup(profileIcon, { size: 22, color: '#757993' }) }))
    ])
  ]);
}

// Below the desktop breakpoint this renders as the familiar bottom tabbar
// (style.css's default rules); at/above it, style.css's media query rotates
// the very same markup into a left-hand sidebar — home at the top, settings
// pushed to the bottom via `.tab-settings { margin-top: auto }`, with a
// collapse toggle appended after it (feature-ui.md's Desktop section).
// One shared component, not two, so the two layouts can never drift apart
// on which screens/icons/order they offer.
function renderTabbar(state) {
  const collapsed = state.sidebarCollapsed;
  const tabs = TABS.map((tab) => {
    const active = tab.screen === state.screen;
    const onclick = tab.screen === 'receive' ? goReceive : tab.screen === 'move' ? goSend : () => go(tab.screen);
    const label = t(tab.labelKey);
    return h('button', {
      class: 'tab' + (active ? ' active' : '') + (tab.screen === 'settings' ? ' tab-settings' : ''),
      'aria-label': label, onclick
    }, [
      h('span', { html: iconMarkup(tab.icon, { size: 20, color: active ? '#4D4DFF' : '#757993' }) }),
      h('span', { class: 'tab-label' }, label)
    ]);
  });
  const collapseToggle = h('button', {
    class: 'tab sidebar-collapse-toggle', 'aria-label': collapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar'),
    onclick: toggleSidebar
  }, h('span', { html: iconMarkup(collapsed ? 'chevronRight' : 'chevronLeft', { size: 20, color: '#757993' }) }));
  return h('div', { class: 'tabbar' + (collapsed ? ' tabbar-collapsed' : '') }, [...tabs, collapseToggle]);
}

function renderUpdateBanner() {
  return h('div', { class: 'update-banner' }, [
    h('span', {}, t('header.updateAvailable')),
    h('button', { class: 'btn btn-primary btn-sm', onclick: applySwUpdate }, t('header.refresh'))
  ]);
}

function renderWorkerBanner() {
  return h('div', { class: 'update-banner banner-warn' }, [
    h('span', {}, t('header.workerUnavailable')),
    h('button', { class: 'btn btn-secondary btn-sm', onclick: retryWorkerConnection }, t('common.retry'))
  ]);
}

function renderBannerStack(state) {
  const items = [];
  if (state.swUpdateAvailable) items.push(renderUpdateBanner());
  if (state.workerConnected === false) items.push(renderWorkerBanner());
  return items.length ? h('div', { class: 'banner-stack' }, items) : null;
}

function renderApp(state) {
  const renderScreen = SCREENS[state.screen] || renderHome;
  return h('div', { class: 'app-shell' }, [
    renderHeader(state),
    // .app-main's own flex `order` (style.css) puts the tabbar last visually
    // on mobile (bottom bar) and first on desktop (left sidebar) regardless
    // of DOM order here — see the media query for why source order doesn't
    // need to change between the two.
    h('div', { class: 'app-main' }, [
      renderTabbar(state),
      h('div', { class: 'screen-body' }, [renderScreen()])
    ])
  ]);
}

const MODAL_FLAGS = ['showOpenAccount', 'showAddRecipient', 'showImportKey', 'showConfirmReveal', 'showStatusPanel', 'showProfileMenu', 'showLicense', 'showMainnetConfirm', 'showVerifyBackup', 'showMigrateTreasury', 'showLanguagePicker'];
let openModalFlag = null;

// Removing a focused node from the document (which clear() does on every
// render) synchronously fires a native 'blur' event on it. If that blur
// handler itself calls setState (e.g. inline validation), render() gets
// re-entered while the outer call is still mid-teardown/rebuild — clear()'s
// live child walk and the outer appendChild calls end up fighting over a
// root that a nested render() already mutated, throwing NotFoundError.
// Guarding here (rather than at each call site) protects every render path,
// not just the one that happened to surface it.
let rendering = false;
let renderQueued = false;

// Fields that must re-render on every keystroke (live search/filtering,
// unlike the setSilent text inputs elsewhere) opt in via
// data-preserve-focus="<key>". clear() below tears down and rebuilds the
// whole DOM on every render, which would otherwise steal focus and the
// caret position — and dismiss the on-screen keyboard on mobile — after
// every character typed.
function capturePreserveFocus() {
  const active = document.activeElement;
  const key = active && active.dataset ? active.dataset.preserveFocus : null;
  if (!key) return null;
  const hasSelection = 'selectionStart' in active;
  return {
    key,
    selStart: hasSelection ? active.selectionStart : null,
    selEnd: hasSelection ? active.selectionEnd : null
  };
}

function restorePreserveFocus(root, captured) {
  if (!captured) return;
  const el = root.querySelector(`[data-preserve-focus="${captured.key}"]`);
  if (!el) return;
  el.focus();
  if (captured.selStart != null && 'setSelectionRange' in el) {
    try { el.setSelectionRange(captured.selStart, captured.selEnd); } catch { /* not applicable to this input type */ }
  }
}

export function render(root) {
  if (rendering) { renderQueued = true; return; }
  rendering = true;
  try {
    const state = getState();
    if (state.theme === 'light' || state.theme === 'dark') {
      document.documentElement.setAttribute('data-theme', state.theme);
    } else {
      document.documentElement.removeAttribute('data-theme');
    }
    // lang drives assistive-tech pronunciation/voice selection; dir flips
    // the whole layout for Arabic/Urdu (see src/i18n/languages.js's rtl
    // flags and style.css's [dir="rtl"] overrides) — both live outside
    // #app's own subtree, same reasoning as data-theme above.
    document.documentElement.setAttribute('lang', state.language);
    document.documentElement.setAttribute('dir', isRTL(state.language) ? 'rtl' : 'ltr');
    const preserved = capturePreserveFocus();
    clear(root);

    let main;
    if (state.screen === 'landing') main = renderLanding();
    else main = renderApp(state);
    root.appendChild(main);

    const banners = renderBannerStack(state);
    if (banners) root.appendChild(banners);

    const overlays = [
      renderOpenAccountModal(), renderAddRecipientModal(), renderImportKeyModal(),
      renderConfirmRevealModal(), renderStatusPanel(), renderProfileMenu(), renderLicenseModal(),
      renderMainnetConfirmModal(), renderVerifyBackupModal(), renderMigrateTreasuryModal(),
      renderLanguagePicker()
    ].filter(Boolean);
    overlays.forEach((el) => root.appendChild(el));

    updateToast(state.toast);
    restorePreserveFocus(root, preserved);

    // Autofocus the first focusable element only on the closed->open
    // transition, not on every re-render while a modal is already open
    // (which would steal focus back from whatever the user is doing).
    const nowOpenFlag = MODAL_FLAGS.find((f) => state[f]) || null;
    if (nowOpenFlag && nowOpenFlag !== openModalFlag) {
      const el = root.querySelector('.modal-card input, .modal-card textarea, .modal-card select, .modal-card button');
      if (el) requestAnimationFrame(() => el.focus());
    }
    openModalFlag = nowOpenFlag;
  } finally {
    rendering = false;
  }
  if (renderQueued) {
    renderQueued = false;
    render(root);
  }
}
